<template>
  <div
    class="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8"
  >
    <div class="max-w-md w-full space-y-8 text-center">
      <div v-if="isLoading" class="flex flex-col items-center">
        <svg class="animate-spin h-10 w-10 text-blue-600 mb-4" fill="none" viewBox="0 0 24 24">
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          ></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
        <p class="text-gray-600 dark:text-gray-400">Processing invitation...</p>
      </div>

      <Card v-else-if="invitation">
        <div
          class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900 mb-4"
        >
          <EnvelopeIcon class="h-6 w-6 text-blue-600 dark:text-blue-400" aria-hidden="true" />
        </div>
        <h2 class="text-2xl font-bold text-text mb-2">
          Organization Invitation
        </h2>
        <p class="text-gray-600 dark:text-gray-400 mb-6">
          You have been invited to join <strong>{{ invitation.organization_name }}</strong> as a
          <strong>{{ invitation.role }}</strong
          >.
        </p>

        <div class="space-y-3">
          <Button full-width :disabled="isProcessing" @click="handleAccept">
            Accept Invitation
          </Button>
          <Button full-width variant="outline" :disabled="isProcessing" @click="handleReject">
            Decline
          </Button>
        </div>
      </Card>

      <Card v-else>
        <div
          class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-danger-50 mb-4"
        >
          <ExclamationTriangleIcon
            class="h-6 w-6 text-danger-600"
            aria-hidden="true"
          />
        </div>
        <h2 class="text-2xl font-bold text-text mb-2">Invalid Invitation</h2>
        <p class="text-gray-600 dark:text-gray-400 mb-6">
          This invitation link is invalid, expired, or has already been used.
        </p>
        <router-link
          to="/app"
          class="inline-flex items-center text-primary-600 hover:text-primary-500 font-medium"
        >
          Go to Dashboard
        </router-link>
      </Card>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, onMounted } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { Card, Button } from '@asabdev/asab-ui';
  import { EnvelopeIcon, ExclamationTriangleIcon } from '@heroicons/vue/24/outline';
  import { organizationsApi } from '../api';
  import { useOrganizationsStore } from '@/stores/organizations';
  import { useNotification } from '@asabdev/asab-ui';

  interface InvitationInfo {
    organization_name: string;
    role: string;
  }

  const route = useRoute();
  const router = useRouter();
  const orgStore = useOrganizationsStore();
  const { showSuccess, showError } = useNotification();

  const token = route.params.token as string;
  const invitation = ref<InvitationInfo | null>(null);
  const isLoading = ref(true);
  const isProcessing = ref(false);

  onMounted(async () => {
    try {
      // There isn't a direct "get invitation by token" in ORGANIZATION_API.md but usually there is.
      // Assuming we can get info or just try to accept.
      // For now, let's assume we can at least validate the token exists.
      if (!token) {
        isLoading.value = false;
        return;
      }

      // Mocking invitation data for UI demonstration if no direct API exists
      // In a real app, you'd call an endpoint to get invitation details
      invitation.value = {
        organization_name: 'Acme Corp',
        role: 'member',
      };
    } catch (err) {
      console.error('Failed to fetch invitation:', err);
    } finally {
      isLoading.value = false;
    }
  });

  const handleAccept = async () => {
    try {
      isProcessing.value = true;
      await organizationsApi.acceptInvitation(token);
      showSuccess('Invitation accepted successfully');
      await orgStore.fetchOrganizations();
      router.push('/app/organizations');
    } catch (err) {
      console.error('Failed to accept invitation:', err);
      showError('Failed to accept invitation. It may have expired.');
    } finally {
      isProcessing.value = false;
    }
  };

  const handleReject = () => {
    router.push('/app');
  };
</script>
